import { useEffect, useRef } from 'react';
import type { PatientPhoneStatus, SipCallState } from '@doxyme/types';
import { CallState, CallTransition } from '../../../features/call/call.types';
import type { TransitionFunc } from '../useCall.types';
import { getAllSessionParticipantsMap } from '../../../features/session/session.selectors';
import { useAppSelector } from '../../useReduxToolkit';
import { getLogger } from '../../../libs/logger';

const logger = getLogger('useCallStatePhoneCallParticipants');

/** Statuses that end the ringing phase as patient declined. */
const PATIENT_PHONE_DECLINED_STATUSES: PatientPhoneStatus[] = [
  'patient_declined',
  'busy',
  // Patient answered and quickly ended the call; declined after answering while still ringing.
  'completed',
];

/** Statuses where the patient did not answer while the call was ringing. */
const PATIENT_PHONE_NO_ANSWER_STATUSES: PatientPhoneStatus[] = [
  'patient_no_answer',
  'unanswered',
  'cancelled',
  'timeout',
];

/** Statuses where the outbound phone call failed to connect. */
const PATIENT_PHONE_FAILED_STATUSES: PatientPhoneStatus[] = [
  'failed',
  'rejected',
];

/** Statuses where the patient left after the call was connected. */
const PATIENT_PHONE_HUNG_UP_STATUSES: PatientPhoneStatus[] = ['completed'];

type SipParticipantSnapshot = {
  id: string;
  sipCallState?: SipCallState | null;
  patientPhoneStatus?: PatientPhoneStatus;
};

/**
 * Watches the outbound SIP phone participant while the call is ringing or in progress,
 * and drives the phone-call slice of the call state machine from their Vonage leg status.
 */
export const useCallStatePhoneCallParticipants = (
  transition: TransitionFunc,
  callState: CallState,
): void => {
  const allParticipantsMap = useAppSelector(getAllSessionParticipantsMap);

  const phoneParticipant = Object.values(allParticipantsMap).find(
    (participant) => participant.isSipParticipant,
  ) as SipParticipantSnapshot | undefined;

  const { sipCallState, patientPhoneStatus } = phoneParticipant ?? {};

  const isRinging = callState === CallState.PhoneCallRinging;
  const isPhoneCallInProgress = callState === CallState.PhoneCallInProgress;
  const isPhoneCallMonitored = isRinging || isPhoneCallInProgress;

  // Each phase fires at most one transition per ringing / in-progress cycle.
  const hasEmittedRingingTransition = useRef(false);
  const hasEmittedInProgressTransition = useRef(false);

  useEffect(() => {
    if (!isPhoneCallMonitored) {
      hasEmittedRingingTransition.current = false;
      hasEmittedInProgressTransition.current = false;
      return;
    }

    // Once a phase has emitted its transition, ignore later status updates.
    if (isRinging && hasEmittedRingingTransition.current) {
      return;
    }
    if (isPhoneCallInProgress && hasEmittedInProgressTransition.current) {
      return;
    }

    if (patientPhoneStatus === undefined) {
      return;
    }

    let nextTransition: CallTransition | undefined;

    if (isRinging) {
      // Conditions are evaluated in priority order; the first match wins.
      if (sipCallState === 'ACTIVE' && patientPhoneStatus === 'answered') {
        nextTransition = CallTransition.PHONE_PARTICIPANT_ANSWERED;
      } else if (PATIENT_PHONE_FAILED_STATUSES.includes(patientPhoneStatus)) {
        nextTransition = CallTransition.PHONE_CALL_PATIENT_FAILED;
      } else if (
        PATIENT_PHONE_NO_ANSWER_STATUSES.includes(patientPhoneStatus)
      ) {
        nextTransition = CallTransition.PHONE_CALL_PATIENT_UNANSWERED;
      } else if (PATIENT_PHONE_DECLINED_STATUSES.includes(patientPhoneStatus)) {
        nextTransition = CallTransition.PHONE_CALL_PATIENT_DECLINED;
      }
    } else if (PATIENT_PHONE_FAILED_STATUSES.includes(patientPhoneStatus)) {
      nextTransition = CallTransition.PHONE_CALL_PATIENT_FAILED;
    } else if (PATIENT_PHONE_HUNG_UP_STATUSES.includes(patientPhoneStatus)) {
      nextTransition = CallTransition.PHONE_CALL_PATIENT_LEFT;
    }

    if (!nextTransition) {
      return;
    }

    if (isRinging) {
      hasEmittedRingingTransition.current = true;
    } else {
      hasEmittedInProgressTransition.current = true;
    }

    logger.info(nextTransition, {
      participantId: phoneParticipant?.id,
      sipCallState,
      patientPhoneStatus,
      callState,
    });
    void transition(nextTransition);
  }, [
    callState,
    isPhoneCallInProgress,
    isPhoneCallMonitored,
    isRinging,
    patientPhoneStatus,
    phoneParticipant?.id,
    sipCallState,
    transition,
  ]);
};
